import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { StepProps } from "./types"

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors cursor-pointer ${checked ? "bg-primary" : "bg-slate-300 dark:bg-slate-600"}`}>
      <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-card shadow transition-transform ${checked ? "translate-x-5.5" : "translate-x-0.5"}`} />
    </button>
  )
}

interface EngagementRowProps {
  icon: React.ReactNode
  label: string
  description: string
  checked: boolean
  onChange: (v: boolean) => void
  children?: React.ReactNode
}

function EngagementRow({ icon, label, description, checked, onChange, children }: EngagementRowProps) {
  return (
    <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border">
      <div className="flex items-center gap-4 p-4">
        <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900 dark:text-foreground/80 dark:text-white">{label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
        <Toggle checked={checked} onChange={onChange} />
      </div>
      {checked && children && (
        <div className="px-4 pb-4 border-t border-stroke-100 dark:border-border dark:border-slate-800 pt-3">
          {children}
        </div>
      )}
    </div>
  )
}

export function StepEngagement({ data, onChange }: StepProps) {
  const t = useTranslation()
  return (
    <div className="space-y-2">
      <div className="mb-2">
        <p className="text-sm text-slate-500">{t("webinar.chooseEngagementDesc")}</p>
      </div>

      {/* Chat */}
      <EngagementRow
        icon={<img src="/banners/cht.svg" alt="Chat" className="w-6 h-6" />}
        label={t("webinar.chat")}
        description={t("webinar.chatDesc")}
        checked={data.chat}
        onChange={(v) => onChange({ chat: v })}
      >
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-600 dark:text-slate-400">{t("webinar.chatVisibility")}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${data.chatVisibility === "host" ? "border-primary bg-primary/5 text-primary" : "border-stroke-100 dark:border-border dark:border-border text-slate-700 dark:text-slate-300"}`}>
              <input type="radio" name="chatVisibility" checked={data.chatVisibility === "host"}
                onChange={() => onChange({ chatVisibility: "host" })} className="accent-primary" />
              <div>
                <p className="font-medium text-xs">{t("webinar.onlyHost")}</p>
                <p className="text-xs opacity-70 mt-0.5">{t("webinar.messagesVisibleHost")}</p>
              </div>
            </label>
            <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${data.chatVisibility === "attendees" ? "border-primary bg-primary/5 text-primary" : "border-stroke-100 dark:border-border dark:border-border text-slate-700 dark:text-slate-300"}`}>
              <input type="radio" name="chatVisibility" checked={data.chatVisibility === "attendees"}
                onChange={() => onChange({ chatVisibility: "attendees" })} className="accent-primary" />
              <div>
                <p className="font-medium text-xs">{t("webinar.allAttendees")}</p>
                <p className="text-xs opacity-70 mt-0.5">{t("webinar.messagesVisibleEveryone")}</p>
              </div>
            </label>
          </div>
        </div>
      </EngagementRow>

      {/* Live Q&A */}
      <EngagementRow
        icon={<img src="/banners/qa.svg" alt="Live Q&A" className="w-6 h-6" />}
        label={t("webinar.liveQA")}
        description={t("webinar.liveQADesc")}
        checked={data.liveQandA}
        onChange={(v) => onChange({ liveQandA: v })}
      />

      {/* Reactions */}
      <EngagementRow
        icon={<img src="/banners/react.svg" alt="Reactions" className="w-6 h-6" />}
        label={t("webinar.reactionsLabel")}
        description={t("webinar.reactionsDesc")}
        checked={data.reactions}
        onChange={(v) => onChange({ reactions: v })}
      />

      {/* Polls */}
      <EngagementRow
        icon={<img src="/banners/poll.svg" alt="Polls" className="w-6 h-6" />}
        label={t("webinar.polls")}
        description={t("webinar.pollsDesc")}
        checked={data.pollsEnabled}
        onChange={(v) => onChange({ pollsEnabled: v })}
      />

      {/* Resources */}
      <EngagementRow
        icon={<img src="/banners/resource.svg" alt="Resources" className="w-6 h-6" />}
        label={t("webinar.resources")}
        description={t("webinar.resourcesDesc")}
        checked={data.resources}
        onChange={(v) => onChange({ resources: v })}
      />

      {/* Surveys */}
      <EngagementRow
        icon={<img src="/banners/survey.svg" alt="Surveys" className="w-6 h-6" />}
        label={t("webinar.surveys")}
        description={t("webinar.surveysDesc")}
        checked={data.surveysEnabled}
        onChange={(v) => onChange({ surveysEnabled: v })}
      />
    </div>
  )
}
